import { memo } from 'react';
import type { BoardPoint, StoneColor, TimedGameSnapshot } from '@wx-weiqi/contracts';

import { pointKey } from '../domain/board-geometry';
import { revealAuthoritativeScoringResult } from '../domain/scoring-presentation';
import { BoardSurface } from './BoardSurface';

interface GoBoardProps {
  readonly keyboardNavigation?: boolean;
  readonly previewPoint?: BoardPoint | null;
  readonly boardAppearance?: 'classic' | 'wood';
  readonly snapshot: TimedGameSnapshot;
  readonly disabled: boolean;
  readonly scoringMode: boolean;
  readonly scoringTargetColor: StoneColor | null;
  readonly selectedDisputedStones: ReadonlySet<string>;
  readonly lastMove: BoardPoint | null;
  readonly onPoint: (point: BoardPoint) => void;
}

const EMPTY_DEAD_STONES = new Set<string>();

export const GoBoard = memo(function GoBoard({
  keyboardNavigation = false,
  previewPoint = null,
  boardAppearance = 'classic',
  snapshot,
  disabled,
  scoringMode,
  scoringTargetColor,
  selectedDisputedStones,
  lastMove,
  onPoint,
}: GoBoardProps) {
  const serverDead = new Set(
    snapshot.scoring?.proposal?.deadStones.map((point) => pointKey(point)) ?? [],
  );
  const proposal = snapshot.scoring?.proposal;
  const showFinalScoring = revealAuthoritativeScoringResult(snapshot.status, proposal?.submittedBy);

  return (
    <BoardSurface
      keyboardNavigation={keyboardNavigation}
      previewPoint={previewPoint}
      boardAppearance={boardAppearance}
      emphasizeLastMove={boardAppearance === 'wood'}
      size={snapshot.board.size}
      stones={snapshot.board.stones}
      blackTerritory={showFinalScoring ? (proposal?.black.territory ?? []) : []}
      whiteTerritory={showFinalScoring ? (proposal?.white.territory ?? []) : []}
      deadStoneKeys={showFinalScoring ? serverDead : EMPTY_DEAD_STONES}
      selectedStoneKeys={selectedDisputedStones}
      lastMove={lastMove}
      isActionable={(_point, stone) =>
        !disabled && (scoringMode ? stone !== null && stone === scoringTargetColor : stone === null)
      }
      onPoint={onPoint}
    />
  );
});
